import React, { useState, useEffect } from 'react';
import { getTodayCashRegister, openCashRegister, closeCashRegister, getDailySummary } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Calculator, Loader2, RefreshCw, Banknote, CreditCard, TrendingUp, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { getCashRegisterUiState } from '../registerState';

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && typeof detail.message === 'string' && detail.message.trim()) {
    return detail.message;
  }
  return fallback;
}

export default function PosCashRegister() {
  const [register, setRegister] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [openingAmount, setOpeningAmount] = useState('');
  const [closingAmount, setClosingAmount] = useState('');
  const [notes, setNotes] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [registerData, summaryData] = await Promise.all([
        getTodayCashRegister(),
        getDailySummary()
      ]);
      setRegister(registerData);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenRegister = async () => {
    if (!openingAmount) {
      toast.error('Bitte Anfangsbestand eingeben');
      return;
    }
    try {
      await openCashRegister(parseFloat(openingAmount), user?.id);
      toast.success('Kasse geöffnet');
      setShowOpenDialog(false);
      setOpeningAmount('');
      loadData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Fehler beim Öffnen der Kasse'));
    }
  };

  const handleCloseRegister = async () => {
    if (!closingAmount) {
      toast.error('Bitte Kassenstand eingeben');
      return;
    }
    try {
      const result = await closeCashRegister(parseFloat(closingAmount), user?.id, notes);
      if (result.difference !== 0) {
        toast.warning(`Kassendifferenz: €${result.difference.toFixed(2)}`);
      } else {
        toast.success('Kasse abgeschlossen');
      }
      setShowCloseDialog(false);
      setClosingAmount('');
      setNotes('');
      loadData();
    } catch (error) {
      if ([404, 409].includes(error?.response?.status)) {
        loadData();
      }
      toast.error(getApiErrorMessage(error, 'Kasse konnte nicht abgeschlossen werden'));
    }
  };

  const autoCashCloseAmount = register?.auto_cash_close_amount || 0;
  const autoCashCloseOrders = register?.auto_cash_close_orders || 0;
  const expectedCash = (register?.opening_amount || 0) + (register?.cash_sales || 0) + autoCashCloseAmount;
  const registerState = getCashRegisterUiState(register);
  const hasActiveShift = registerState === 'open';
  const isClosed = registerState === 'closed';
  const needsReview = registerState === 'review_required';
  const canOpenNewShift = Boolean(register?.can_open_new_shift) || ['closed', 'not_started'].includes(registerState);
  const registerStatusLabel =
    isClosed
      ? 'Abgeschlossen'
      : hasActiveShift
        ? 'Offen'
        : needsReview
          ? 'Status prüfen'
          : 'Nicht gestartet';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="pos-cash-register">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1a2f38]">Kassenabschluss</h1>
          <p className="text-sm text-muted-foreground">
            {format(new Date(), 'dd. MMMM yyyy', { locale: de })}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Status Card */}
      <Card
        className={`shadow-sm border-l-4 ${
          isClosed
            ? 'border-l-green-500'
            : needsReview
              ? 'border-l-amber-500'
              : hasActiveShift
                ? 'border-l-blue-500'
                : 'border-l-slate-400'
        }`}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Kassenstatus</p>
              <p className="text-xl font-bold">
                {registerStatusLabel}
              </p>
            </div>
            {hasActiveShift ? (
              <Button onClick={() => setShowCloseDialog(true)} className="bg-red-500 hover:bg-red-600">
                Kasse abschließen
              </Button>
            ) : canOpenNewShift ? (
              <Button onClick={() => setShowOpenDialog(true)} className="bg-[#1a2f38]">
                Neue Kasse öffnen
              </Button>
            ) : (
              <Button disabled className="bg-slate-300 text-muted-foreground">
                Status prüfen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Banknote className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">Barzahlung</p>
                <p className="text-2xl font-bold text-green-700">€{register?.cash_sales?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">Kartenzahlung</p>
                <p className="text-2xl font-bold text-blue-700">€{register?.card_sales?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-muted-foreground">Gesamtumsatz</p>
                <p className="text-2xl font-bold text-purple-700">€{register?.total_sales?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calculator className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Bestellungen</p>
                <p className="text-2xl font-bold">{register?.total_orders || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cash Calculation */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Kassenberechnung</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex justify-between py-2 border-b">
            <span>Anfangsbestand</span>
            <span className="font-mono">€{register?.opening_amount?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between py-2 border-b">
            <span>+ Bareinnahmen</span>
            <span className="font-mono text-green-600">€{register?.cash_sales?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="flex justify-between py-2 font-bold text-lg">
            <span>= Soll-Bestand</span>
            <span className="font-mono">€{expectedCash.toFixed(2)}</span>
          </div>
          
          {isClosed && (
            <>
              <div className="flex justify-between py-2 border-t">
                <span>Ist-Bestand</span>
                <span className="font-mono">€{register?.closing_amount?.toFixed(2) || '0.00'}</span>
              </div>
              <div className={`flex justify-between py-2 font-bold ${register?.difference !== 0 ? 'text-red-600' : 'text-green-600'}`}>
                <span>Differenz</span>
                <span className="font-mono">€{register?.difference?.toFixed(2) || '0.00'}</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Stornos */}
      {register?.total_cancellations > 0 && (
        <Card className="shadow-sm border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <div>
                <p className="font-medium">{register.total_cancellations} Stornierungen</p>
                <p className="text-sm text-muted-foreground">Betrag: €{register.cancellation_amount?.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Open Dialog */}
      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kasse öffnen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Anfangsbestand (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={openingAmount}
                onChange={(e) => setOpeningAmount(e.target.value)}
                placeholder="0.00"
                className="text-2xl font-mono"
              />
            </div>
            <Button onClick={handleOpenRegister} className="w-full bg-[#1a2f38]">
              Kasse öffnen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kasse abschließen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Erwarteter Kassenbestand</p>
              <p className="text-2xl font-bold font-mono">€{expectedCash.toFixed(2)}</p>
            </div>
            {autoCashCloseOrders > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                Beim Abschluss werden {autoCashCloseOrders} offene Bestellung{autoCashCloseOrders === 1 ? '' : 'en'} automatisch als Barzahlung abgeschlossen (€{autoCashCloseAmount.toFixed(2)}).
              </div>
            )}
            <div>
              <Label>Tatsächlicher Kassenbestand (€)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={closingAmount}
                onChange={(e) => setClosingAmount(e.target.value)}
                placeholder={expectedCash.toFixed(2)}
                className="text-2xl font-mono"
              />
            </div>
            <div>
              <Label>Notizen (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Bemerkungen zum Abschluss"
              />
            </div>
            <Button onClick={handleCloseRegister} className="w-full bg-red-500 hover:bg-red-600">
              Kasse abschließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
